import { useEffect, useState } from 'react';
import { Megaphone, X } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';

interface Banner {
  id: string;
  subject: string;
  bodyText: string;
  sentAt: string;
}

interface Props {
  audience: 'agent' | 'contact';
}

const POLL_MS = 5 * 60_000;

// Top-of-page strip showing active admin broadcasts the current user
// hasn't dismissed. Pulled on mount and every 5 minutes. Each card has
// an X to dismiss; dismissals are per-user and persist server-side.
export function BroadcastBanner({ audience }: Props): React.ReactElement | null {
  const [banners, setBanners] = useState<Banner[]>([]);
  const base = audience === 'agent' ? '/api/banners' : '/api/portal/banners';

  async function load() {
    try {
      const r = await apiFetch<{ banners: Banner[] }>(base);
      setBanners(r.banners);
    } catch (e) {
      // 401 just means the user isn't authenticated yet — drop silently.
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return;
    }
  }

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  async function dismiss(id: string) {
    setBanners((cur) => cur.filter((b) => b.id !== id));
    try {
      await apiFetch(`${base}/${id}/dismiss`, { method: 'POST' });
    } catch {
      // best-effort — if it fails the banner reappears on next poll, which is fine
    }
  }

  if (banners.length === 0) return null;

  return (
    <div className="space-y-0">
      {banners.map((b) => (
        <div
          key={b.id}
          className="flex items-start gap-3 border-b border-[var(--color-t3-blue)]/20 bg-[var(--color-t3-blue)]/10 px-6 py-3"
        >
          <Megaphone className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-t3-blue)]" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-[var(--color-header)]">{b.subject}</div>
            <div className="mt-0.5 whitespace-pre-wrap text-sm text-[var(--color-body)]">
              {b.bodyText}
            </div>
          </div>
          <button
            type="button"
            onClick={() => void dismiss(b.id)}
            aria-label="Dismiss"
            className="shrink-0 rounded p-1 text-[var(--color-muted)] hover:bg-white/40 hover:text-[var(--color-header)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
